import { useCallback, useEffect, useRef, useState } from "react";

// Save once the user has stopped typing for this long
export const AUTOSAVE_DELAY_MS = 2000;
// Keep the autosave icon spinning at least this long so it is noticeable
const MIN_SPIN_MS = 1000;

/**
 * Hook for saving automatically once the user has paused typing.
 *
 * Call `scheduleSave()` on every change - each call restarts the countdown, so
 * nothing is saved while the user is still typing.
 *
 * @param {object} options
 * @param {boolean} options.enabled - Whether autosave is turned on
 * @param {function} options.hasChanges - Returns true if there is something to save
 * @param {function} options.save - Saves the changes; may return a promise
 * @param {number} [options.delay] - Pause (ms) before saving
 * @returns {{ scheduleSave: function, flushSave: function, isAutosaving: boolean }}
 *   `flushSave()` saves pending changes right away (e.g. before switching documents)
 */
export const useAutosave = ({
  enabled,
  hasChanges,
  save,
  delay = AUTOSAVE_DELAY_MS,
}) => {
  const [isAutosaving, setIsAutosaving] = useState(false);
  const timerRef = useRef(null);
  const isMountedRef = useRef(true);

  // Always call the latest callbacks, without restarting the countdown
  const hasChangesRef = useRef(hasChanges);
  const saveRef = useRef(save);
  useEffect(() => {
    hasChangesRef.current = hasChanges;
    saveRef.current = save;
  });

  const cancelScheduledSave = useCallback(() => {
    clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);

  const runSave = useCallback(async () => {
    if (!hasChangesRef.current()) return;

    setIsAutosaving(true);
    const startedAt = Date.now();
    try {
      await saveRef.current();
    } catch (err) {
      console.error("Autosave failed:", err);
    } finally {
      const remaining = MIN_SPIN_MS - (Date.now() - startedAt);
      setTimeout(
        () => {
          if (isMountedRef.current) setIsAutosaving(false);
        },
        Math.max(0, remaining),
      );
    }
  }, []);

  const scheduleSave = useCallback(() => {
    if (!enabled) return;

    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      runSave();
    }, delay);
  }, [enabled, delay, runSave]);

  const flushSave = useCallback(() => {
    if (!timerRef.current) return Promise.resolve();
    cancelScheduledSave();
    return runSave();
  }, [cancelScheduledSave, runSave]);

  // Stop a pending save when autosave is turned off
  useEffect(() => {
    if (!enabled) cancelScheduledSave();
  }, [enabled, cancelScheduledSave]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      cancelScheduledSave();
    };
  }, [cancelScheduledSave]);

  return { scheduleSave, flushSave, isAutosaving };
};
